import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  Users,
  ShoppingCart,
  UserCheck,
  FileText,
  Plus,
  UserPlus,
} from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
} from '@/components/ui/sheet';

interface QuickActions {
  onCreatePatient?: () => void;
  onCreateUser?: () => void;
}

interface UniversalSidebarProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quickActions?: QuickActions;
}

const UniversalSidebar: React.FC<UniversalSidebarProps> = ({ open, onOpenChange, quickActions }) => {
  const location = useLocation();

  const isActiveRoute = (path: string) => {
    return location.pathname === path;
  };

  const navigationItems = [
    {
      title: "Catalogue",
      path: "/catalogue",
      icon: FileText,
    },
    {
      title: "Patients", 
      path: "/patients",
      icon: Users,
    },
    {
      title: "Orders",
      path: "/orders", 
      icon: ShoppingCart,
    },
    {
      title: "Users",
      path: "/users",
      icon: UserCheck,
    },
    {
      title: "Reports",
      path: "/reports",
      icon: FileText,
    },
  ];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent 
        side="left" 
        className="w-72 md:w-80 lg:w-96 p-0 bg-white"
      >
        <SheetHeader className="border-b border-gray-100 p-4 md:p-6">
          <div className="flex items-center">
            <img 
              src="/large_logo.png" 
              alt="VITL Logo" 
              className="h-8 md:h-10 object-contain"
            />
          </div>
        </SheetHeader>
        
        <div className="p-4 md:p-6">
          <nav className="space-y-2 md:space-y-3">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => onOpenChange(false)}
                  className={`flex items-center space-x-3 md:space-x-4 px-4 md:px-6 py-3 md:py-4 rounded-md text-sm md:text-base font-medium font-lato transition-colors duration-200 ${
                    isActiveRoute(item.path)
                      ? 'bg-vitl-green/10 text-vitl-green border-r-2 border-vitl-green'
                      : 'text-vitl-navy hover:bg-vitl-green/5 hover:text-vitl-green'
                  }`}
                >
                  <Icon className="w-5 h-5 md:w-6 md:h-6 flex-shrink-0" />
                  <span>{item.title}</span>
                </Link>
              );
            })}
          </nav>

          {/* Quick Actions Section */}
          {(quickActions?.onCreatePatient || quickActions?.onCreateUser) && (
            <div className="hidden md:block mt-8 pt-6 border-t border-gray-100">
              <div className="text-sm text-gray-600 font-lato space-y-3">
                <h4 className="font-medium text-vitl-navy">Quick Actions</h4>
                <div className="space-y-2">
                  {quickActions?.onCreatePatient && (
                    <button
                      onClick={() => {
                        quickActions.onCreatePatient?.();
                        onOpenChange(false);
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-vitl-green hover:bg-vitl-green/90 text-white rounded-md font-medium transition-colors duration-200 text-sm"
                    >
                      <Plus className="w-4 h-4" />
                      <span>Create New Patient</span>
                    </button>
                  )}
                  {quickActions?.onCreateUser && (
                    <button
                      onClick={() => {
                        quickActions.onCreateUser?.();
                        onOpenChange(false);
                      }}
                      className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-vitl-navy hover:bg-vitl-navy/90 text-white rounded-md font-medium transition-colors duration-200 text-sm"
                    >
                      <UserPlus className="w-4 h-4" />
                      <span>Create New User</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 border-t border-gray-100 p-4 md:p-6">
          <div className="text-xs md:text-sm text-gray-500 font-lato text-center">
            VITL Platform v1.0
          </div>
          <div className="hidden md:block text-xs text-gray-400 font-lato text-center mt-1">
            Professional Healthcare Management
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default UniversalSidebar;